"use client"

import { useEffect, useState, useCallback, useRef } from "react"
import { useAuthStore } from "@/lib/auth-store"
import { BadgeIcon } from "@/components/badges/BadgeIcon"
import { AvatarWithFrame } from "@/components/avatar-with-frame"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"
import type { BadgeRank } from "@/lib/badges/types"
import type { BadgeFrame } from "@/lib/types/badge-frame"
import { useTranslations } from "next-intl"
import { useModalSlot } from "@/lib/client/modal-coordinator"

type UnlockItem =
  | { kind: "badge"; slug: string; name: string; rank: BadgeRank; description: string }
  | { kind: "frame"; id: string; name: string; frameUrl: string; unlockLabel?: string }

const RANK_LABELS: Record<number, string> = { 1: "Rank I", 2: "Rank II", 3: "Rank III" }

// "Seen" state lives on the account (user_seen_unlocks table) via
// /api/seen-unlocks, not localStorage — otherwise logging in from a
// different browser/device would re-show every popup the user already
// dismissed elsewhere. localStorage is kept as a same-browser fallback only,
// in case the network request fails, so a flaky request doesn't re-show a
// popup the user just dismissed a moment ago.
function seenStorageKey(userId: string) {
  return `goonru-badge-seen-${userId}`
}

function getLocalSeenSet(userId: string): Set<string> {
  try {
    const raw = localStorage.getItem(seenStorageKey(userId))
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set()
  } catch {
    return new Set()
  }
}

function cacheSeenLocally(userId: string, keys: string[]) {
  try {
    const seen = getLocalSeenSet(userId)
    keys.forEach((k) => seen.add(k))
    localStorage.setItem(seenStorageKey(userId), JSON.stringify([...seen]))
  } catch {}
}

async function getSeenSet(userId: string): Promise<Set<string>> {
  try {
    const res = await fetch("/api/seen-unlocks")
    if (res.ok) {
      const { keys } = (await res.json()) as { keys: string[] }
      return new Set(keys ?? [])
    }
  } catch {}
  // Server unavailable — fall back to whatever this browser has seen so we
  // don't spam popups it already showed, even though this won't reflect
  // other browsers/devices.
  return getLocalSeenSet(userId)
}

async function markAllSeen(userId: string, keys: string[]) {
  cacheSeenLocally(userId, keys)
  try {
    await fetch("/api/seen-unlocks", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ keys }),
    })
  } catch {}
}

export function BadgeUnlockNotifier() {
  const t = useTranslations("badgeUnlockNotifier")
  // sessionVerified guards against acting on the locally-cached `user` from
  // localStorage before Supabase has actually confirmed a live session this
  // load — without it, a visitor whose browser has a stale cached identity
  // (e.g. an expired session that was never explicitly signed out) could get
  // shown someone else's earned-badge popups despite not actually being
  // signed in. See lib/auth-store.ts's initialize() for where this flips.
  const { user, profile, sessionVerified } = useAuthStore()
  const [queue, setQueue] = useState<UnlockItem[]>([])
  const [equipping, setEquipping] = useState(false)
  // Only one in-house popup may be on screen at a time (lib/client/modal-coordinator.ts).
  const canShow = useModalSlot("badgeUnlock", queue.length > 0)
  // Unlocks are recorded as "seen" when actually shown, not when fetched, so one
  // that is waiting for its turn (or past the per-visit cap) isn't lost.
  const queuedKeys = useRef(new Set<string>())
  const pendingSeenKeys = useRef<string[]>([])

  const current = queue[0] ?? null

  const dismiss = useCallback(() => setQueue((q) => q.slice(1)), [])
  // Everything queued was already recorded as seen the moment the first popup appeared, so clearing the queue is all
  // "skip all" needs (nothing comes back on the next visit).
  const skipAll = useCallback(() => setQueue([]), [])

  const equipFrame = useCallback(async (frameUrl: string) => {
    setEquipping(true)
    try {
      const res = await fetch("/api/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ badge_frame_url: frameUrl }),
      })
      if (!res.ok) throw new Error()
      await useAuthStore.getState().refreshProfile()
      toast.success(t("frameEquipped"))
    } catch {
      toast.error(t("failedToEquipFrame"))
    } finally {
      setEquipping(false)
      dismiss()
    }
  }, [dismiss])

  useEffect(() => {
    if (!sessionVerified || !user?.id) return
    const userId = user.id

    const check = async () => {
      const seen = await getSeenSet(userId)
      const newItems: UnlockItem[] = []
      const newKeys: string[] = []
      const silentKeys: string[] = []

      // Check earned badges
      try {
        const res = await fetch(`/api/badges/${userId}`)
        if (res.ok) {
          const { badges } = await res.json()
          for (const badge of (badges ?? []) as { slug: string; name: string; rank: number; description?: string }[]) {
            const key = `badge:${badge.slug}_r${badge.rank}`
            if (!seen.has(key) && !queuedKeys.current.has(key)) {
              newItems.push({ kind: "badge", slug: badge.slug, name: badge.name, rank: badge.rank as BadgeRank, description: badge.description ?? "" })
              newKeys.push(key)
            }
          }
        }
      } catch {}

      // Check unlocked frames
      try {
        const res = await fetch("/api/badge-frames")
        if (res.ok) {
          const frames = (await res.json()) as BadgeFrame[]
          for (const frame of frames) {
            if (!frame.unlocked) continue
            const key = `frame:${frame.id}`
            if (!seen.has(key) && !queuedKeys.current.has(key)) {
              // Free starter ring (0 XP, e.g. Goonling I): nothing was earned, so
              // record it as seen but don't throw a "Frame Ring Unlocked" popup at signup.
              if (frame.unlockXp === 0) {
                silentKeys.push(key)
                continue
              }
              newItems.push({ kind: "frame", id: frame.id, name: frame.name, frameUrl: frame.frameUrl, unlockLabel: frame.unlockLabel })
              newKeys.push(key)
            }
          }
        }
      } catch {}

      if (silentKeys.length > 0) void markAllSeen(userId, silentKeys)
      if (newItems.length > 0) {
        newKeys.forEach((k) => queuedKeys.current.add(k))
        pendingSeenKeys.current.push(...newKeys)
        setQueue((q) => [...q, ...newItems])
      }
    }

    check()
  }, [sessionVerified, user?.id])

  // The popup is actually on screen now: record what it is showing as seen.
  useEffect(() => {
    if (!canShow || !user?.id || pendingSeenKeys.current.length === 0) return
    const keys = pendingSeenKeys.current
    pendingSeenKeys.current = []
    void markAllSeen(user.id, keys)
  }, [canShow, queue.length, user?.id])

  if (!current || !canShow) return null

  const avatarSrc = profile?.profile_picture_url ?? undefined
  const avatarFallback = (profile?.username?.[0] ?? "?").toUpperCase()

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-blue-950/65 backdrop-blur-sm">
      <div className="bg-card border rounded-2xl shadow-2xl w-full max-w-sm animate-in zoom-in-95 fade-in duration-300">
        {/* Celebratory top bar — needs its own overflow clip for the rounded corner */}
        <div className="h-1.5 bg-gradient-to-r from-purple-500 via-amber-400 to-purple-500 rounded-t-2xl" />

        <div className="px-6 pb-6 pt-4 flex flex-col items-center gap-4 text-center">
          {/* Category label */}
          <p className="text-[11px] font-semibold tracking-widest uppercase text-muted-foreground">
            {current.kind === "badge" ? "✦ Badge Unlocked ✦" : "✦ Frame Ring Unlocked ✦"}
          </p>

          {/* Art */}
          {current.kind === "badge" ? (
            <div className="ring-4 ring-amber-400/30 rounded-xl">
              <BadgeIcon slug={current.slug} rank={current.rank} size="xl" />
            </div>
          ) : (
            // Extra vertical space so the frame ring (which extends outside the
            // 120px avatar container via absolute positioning) isn't clipped.
            <div className="flex items-center justify-center" style={{ width: 220, height: 220 }}>
              <AvatarWithFrame
                src={avatarSrc}
                fallback={avatarFallback}
                frameUrl={current.frameUrl}
                size={120}
              />
            </div>
          )}

          {/* Name + rank */}
          <div className="space-y-1">
            <h2 className="text-xl font-bold leading-tight">
              {current.name}
              {current.kind === "badge" && (
                <span className="ml-2 text-sm font-semibold text-primary">{RANK_LABELS[current.rank]}</span>
              )}
            </h2>
            <p className="text-sm text-muted-foreground leading-snug">
              {current.kind === "badge" ? current.description : (current.unlockLabel ?? "")}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2 w-full">
            {current.kind === "frame" ? (
              <>
                <Button variant="outline" className="flex-1" onClick={dismiss} disabled={equipping}>
                  {t("later")}
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => equipFrame((current as Extract<UnlockItem, { kind: "frame" }>).frameUrl)}
                  disabled={equipping}
                >
                  {equipping ? t("equipping") : t("equipNow")}
                </Button>
              </>
            ) : (
              <Button className="flex-1" onClick={dismiss}>
                {t("awesome")}
              </Button>
            )}
          </div>

          {queue.length > 1 && (
            <div className="flex flex-col items-center gap-1.5">
              <p className="text-xs text-muted-foreground">
                {t("moreUnlocksAfterThis", { count: queue.length - 1 })}
              </p>
              <button
                type="button"
                onClick={skipAll}
                disabled={equipping}
                className="text-xs font-medium text-primary underline-offset-2 hover:underline disabled:opacity-50"
              >
                {t("skipAll")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
